import { RefObject, useCallback, useRef, useState } from 'react';

export interface ElementSize {
    width: number;
    height: number;
}

// Misura un elemento e lo rimisura quando cambia. È un ref a funzione e non un effetto perché
// l'elemento può comparire molto dopo il primo disegno di chi lo possiede — dietro un caricamento,
// dietro un Suspense — e un effetto non se ne accorgerebbe. La prima misura arriva prima del disegno.
//
// `forwardTo` riceve lo stesso elemento, per chi ha già un ref suo da tenere.
export const useElementSize = <T extends HTMLElement>(forwardTo?: RefObject<T | null>) => {
    const [size, setSize] = useState<ElementSize | null>(null);
    const observer = useRef<ResizeObserver | null>(null);

    const ref = useCallback(
        (element: T | null) => {
            if (forwardTo) forwardTo.current = element;
            observer.current?.disconnect();
            observer.current = null;
            if (!element) return;

            const measure = () =>
                setSize((current) =>
                    current?.width === element.clientWidth && current.height === element.clientHeight
                        ? current
                        : { width: element.clientWidth, height: element.clientHeight }
                );
            measure();
            observer.current = new ResizeObserver(measure);
            observer.current.observe(element);
        },
        [forwardTo]
    );

    return [ref, size] as const;
};
